import * as Y from 'yjs';

export type RichMark = { type: string; attrs?: Record<string, unknown> };
export type RichNode = { type: string; attrs?: Record<string, unknown>; content?: RichNode[]; text?: string; marks?: RichMark[] };
export type RichDoc = { type: 'doc'; content: RichNode[] };

const nonEmpty = (o: Record<string, unknown> | undefined) => (o && Object.keys(o).length > 0 ? o : undefined);

/** y-prosemirror keys overlapping marks of one type as `type--hash`. */
const markType = (key: string) => key.replace(/--.*$/, '');

function textNodes(t: Y.XmlText): RichNode[] {
  const out: RichNode[] = [];
  for (const op of t.toDelta() as { insert: unknown; attributes?: Record<string, Record<string, unknown> | null> }[]) {
    if (typeof op.insert !== 'string' || op.insert === '') continue; // embeds are not part of the schema
    const marks = Object.entries(op.attributes ?? {}).map(([k, attrs]): RichMark => {
      const a = nonEmpty(attrs ?? undefined);
      return a ? { type: markType(k), attrs: a } : { type: markType(k) };
    });
    out.push(marks.length ? { type: 'text', text: op.insert, marks } : { type: 'text', text: op.insert });
  }
  return out;
}

function children(parent: Y.XmlFragment | Y.XmlElement): RichNode[] {
  const out: RichNode[] = [];
  for (const child of parent.toArray()) {
    if (child instanceof Y.XmlText) out.push(...textNodes(child));
    else if (child instanceof Y.XmlElement) out.push(elementNode(child));
  }
  return out;
}

function elementNode(el: Y.XmlElement): RichNode {
  const node: RichNode = { type: el.nodeName };
  const attrs = nonEmpty(el.getAttributes() as Record<string, unknown>);
  if (attrs) node.attrs = attrs;
  const content = children(el);
  if (content.length) node.content = content;
  return node;
}

/** ProseMirror-style JSON of a fragment, with the encoding y-prosemirror uses (elements, attributes, marks as text attributes). */
export function fragmentToJSON(f: Y.XmlFragment): RichDoc {
  return { type: 'doc', content: children(f) };
}

function fill(parent: Y.XmlFragment | Y.XmlElement, nodes: readonly RichNode[]): void {
  const items: (Y.XmlElement | Y.XmlText)[] = [];
  let run: Y.XmlText | null = null;
  const runs: { t: Y.XmlText; ops: RichNode[] }[] = [];
  const els: { el: Y.XmlElement; node: RichNode }[] = [];
  for (const n of nodes) {
    if (n.type === 'text') {
      if (!run) {
        run = new Y.XmlText();
        items.push(run);
        runs.push({ t: run, ops: [] });
      }
      runs.at(-1)!.ops.push(n);
      continue;
    }
    run = null;
    const el = new Y.XmlElement(n.type);
    items.push(el);
    els.push({ el, node: n });
  }
  parent.insert(0, items);
  for (const { t, ops } of runs) {
    let at = 0;
    for (const op of ops) {
      const text = op.text ?? '';
      const attributes = Object.fromEntries((op.marks ?? []).map((m) => [m.type, m.attrs ?? {}]));
      t.insert(at, text, attributes);
      at += text.length;
    }
  }
  for (const { el, node } of els) {
    for (const [k, v] of Object.entries(node.attrs ?? {})) if (v !== null && v !== undefined) el.setAttribute(k, v as string);
    fill(el, node.content ?? []);
  }
}

/** Writes `doc` into an empty fragment that already belongs to a Y.Doc (inverse of `fragmentToJSON`). */
export function fillFragment(f: Y.XmlFragment, doc: RichDoc): void {
  fill(f, doc.content);
}

const inlineText = (n: RichNode): string =>
  n.type === 'text' ? (n.text ?? '') : n.type === 'hardBreak' ? '\n' : (n.content ?? []).map(inlineText).join('');

const isTextBlock = (n: RichNode) =>
  n.type === 'paragraph' || n.type === 'heading' || (n.content ?? []).some((c) => c.type === 'text' || c.type === 'hardBreak');

/** Plain text: one line per text block (paragraphs, headings, list items' paragraphs). */
export function richToPlain(doc: RichDoc): string {
  const lines: string[] = [];
  const walk = (n: RichNode) => {
    if (isTextBlock(n)) lines.push(inlineText(n));
    else (n.content ?? []).forEach(walk);
  };
  doc.content.forEach(walk);
  return lines.join('\n');
}

/** One paragraph per line. */
export function plainToRich(text: string): RichDoc {
  return {
    type: 'doc',
    content: text.split('\n').map((line) => (line ? { type: 'paragraph', content: [{ type: 'text', text: line }] } : { type: 'paragraph' })),
  };
}
